/**
 * Formats browsers can actually render in an <img>. SVG is deliberately absent:
 * it is a script-bearing document, and these files come from whoever holds the
 * link. Rendering one inline would run their markup on our origin.
 */
const PREVIEWABLE = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/avif',
  'image/bmp',
  'image/x-icon',
])

export function isPreviewableImage(contentType: string, filename = ''): boolean {
  if (PREVIEWABLE.has(contentType.toLowerCase().split(';')[0].trim())) return true
  // Some clients upload with a generic type; fall back to the extension.
  if (contentType === 'application/octet-stream' || contentType === '') {
    return /\.(jpe?g|png|gif|webp|avif|bmp)$/i.test(filename)
  }
  return false
}

export function isPdf(contentType: string, filename = ''): boolean {
  const ct = contentType.toLowerCase().split(';')[0].trim()
  return ct === 'application/pdf' || /\.pdf$/i.test(filename)
}

export type PreviewKind = 'image' | 'pdf'

/** What kind of preview card a file gets, or null for a plain filename row. */
export function previewKind(contentType: string, filename = ''): PreviewKind | null {
  if (isPreviewableImage(contentType, filename)) return 'image'
  if (isPdf(contentType, filename)) return 'pdf'
  return null
}

/**
 * Rebuilds a link the server generated so its origin matches the address bar.
 *
 * The server derives the origin from the request, which is right in almost every
 * case — but the page itself is the one source that cannot be wrong, so the
 * displayed and copied links are pinned to it. Only the path is kept.
 */
export function toCurrentOrigin(url: string): string {
  try {
    return window.location.origin + new URL(url).pathname
  } catch {
    return url
  }
}

export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—'
  if (n < 1024) return `${n} B`
  const units = ['KB', 'MB', 'GB', 'TB', 'PB']
  let value = n / 1024
  let i = 0
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024
    i++
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[i]}`
}

export function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Compact "2d 4h left" / "12m left" phrasing for deadline countdowns. */
export function formatRemaining(iso: string, now = Date.now()): string {
  const ms = new Date(iso).getTime() - now
  if (ms <= 0) return 'passed'

  const minutes = Math.floor(ms / 60_000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days > 0) return `${days}d ${hours % 24}h left`
  if (hours > 0) return `${hours}h ${minutes % 60}m left`
  if (minutes > 0) return `${minutes}m left`
  return 'less than a minute left'
}

/** Datetime-local inputs speak local time without a zone; convert both ways. */
export function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function fromLocalInput(value: string): string {
  return new Date(value).toISOString()
}
